/**
 * Work Item Yönetimi — WBS × Lokasyon operasyon kayıtları (Faz 29)
 * ==================================================================
 * - Liste + filtre (WBS/Lokasyon/Ekip/Durum)
 * - Toplu oluşturma dialogu (WBS düğümleri × Lokasyonlar kartezyen)
 * - Satır bazlı düzenle (metraj, birim fiyat, ilerleme %, ekip override)
 * - Otomatik ekip yeniden atama butonu
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { SortableTh, useSortableRows } from "@/components/SortableTh";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package, Plus, Stack, MagnifyingGlass, PencilSimple, TrashSimple,
  ArrowsClockwise, ArrowLeft, TreeStructure, MapTrifold, UsersFour,
  X, Check, ChartLineUp,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

const STATUS_META = {
  tanimlandi: { label: "Tanımlandı", cls: "bg-slate-100 text-slate-700" },
  planlandi: { label: "Planlandı", cls: "bg-blue-50 text-[#1B4776]" },
  baslamadi: { label: "Başlamadı", cls: "bg-amber-50 text-amber-700" },
  devam_ediyor: { label: "Devam Ediyor", cls: "bg-[#F7FBE9] text-[#556B10]" },
  tamamlandi: { label: "Tamamlandı", cls: "bg-emerald-50 text-emerald-700" },
  beklemede: { label: "Beklemede", cls: "bg-orange-50 text-orange-700" },
  iptal: { label: "İptal", cls: "bg-rose-50 text-rose-700" },
};

const fmt = (n, dec = 2) =>
  n == null || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("tr-TR",
        { minimumFractionDigits: dec, maximumFractionDigits: dec });

// ---------- WBS / Lokasyon ağaç seçicisi (checkbox) ----------
function TreePicker({ nodes, selected, onToggle, testIdPrefix, emptyLabel, leafIds }) {
  // nodes: [{id, name, parent_id, level}]  hierarchical order
  if (!nodes.length) {
    return <div className="text-[11px] text-slate-500 italic p-3">{emptyLabel}</div>;
  }
  const byParent = {};
  for (const n of nodes) byParent[n.parent_id || ""] = [...(byParent[n.parent_id || ""] || []), n];
  const render = (parentId, depth) => (
    (byParent[parentId] || []).map((n) => {
      const isLeaf = !leafIds || leafIds.has(n.id);
      return (
        <div key={n.id}>
          <div className="flex items-center gap-1.5 hover:bg-slate-50 rounded-sm px-1"
               style={{ paddingLeft: 6 + depth * 12 }}>
            <input type="checkbox"
                   data-testid={`${testIdPrefix}-${n.id}`}
                   checked={selected.includes(n.id)}
                   onChange={() => onToggle(n.id)}
                   className="w-3.5 h-3.5" />
            <span className={`text-xs truncate ${isLeaf ? "text-slate-700 font-medium" : "text-slate-500 italic"}`}>
              {n.name}
            </span>
            {n.category && (
              <span className="text-[8px] uppercase tracking-widest font-bold bg-slate-100 text-slate-500 px-1 rounded-sm">
                {n.category}
              </span>
            )}
            {!isLeaf && (
              <span className="text-[8px] uppercase tracking-widest font-bold text-amber-600"
                    title="Bu düğüm alt kırılımlarına otomatik genişletilir">
                ⇩ ALT KIRILIMLAR
              </span>
            )}
          </div>
          {render(n.id, depth + 1)}
        </div>
      );
    })
  );
  return <div className="text-xs">{render("", 0)}</div>;
}

const emptyForm = {
  wbs_node_id: "", location_id: "", name: "", unit: "",
  quantity: 0, unit_price: 0,
  planned_start: "", planned_finish: "",
  progress_percent: 0, team_id: "", team_id_manual: false,
  status: "tanimlandi", description: "",
};

export default function WorkItems() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const canManage = !!user;
  const confirm = useConfirm();

  const [site, setSite] = useState(null);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_amount: 0, earned_amount: 0, avg_progress: 0 });
  const [leaves, setLeaves] = useState([]); // WBS yaprakları (metraj için gerekli)
  const [wbsTree, setWbsTree] = useState([]); // Tüm WBS düğümleri
  const [phases, setPhases] = useState([]); // Lokasyonlar
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtreler
  const [q, setQ] = useState("");
  const [fWbs, setFWbs] = useState("all");
  const [fLoc, setFLoc] = useState("all");
  const [fTeam, setFTeam] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  // Dialog state (edit / new single)
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Bulk dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkWbs, setBulkWbs] = useState([]);
  const [bulkLoc, setBulkLoc] = useState([]);
  const [bulkForm, setBulkForm] = useState({
    default_quantity: 0, default_unit: "m2", default_unit_price: 0,
    default_planned_start: "", default_planned_finish: "",
  });

  // ---- Load ----
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wiR, wbsR, phaseR, teamR, siteR] = await Promise.all([
        axios.get(`${API}/work-items`, { params: { site_id: siteId } }),
        axios.get(`${API}/sites/${siteId}/wbs-progress/matrix`),
        axios.get(`${API}/sites/${siteId}/phases`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/sites/${siteId}/project`),
      ]);
      setSite({ id: siteId, name: siteR.data.site_name, code: siteR.data.site_code });
      setItems(wiR.data.items || []);
      setTotals({
        total_amount: wiR.data.total_amount || 0,
        earned_amount: wiR.data.earned_amount || 0,
        avg_progress: wiR.data.avg_progress || 0,
      });
      setLeaves(wbsR.data.leaves || []);
      // WBS full tree — flat with parent_id
      const wbsFull = await axios.get(`${API}/sites/${siteId}/wbs-nodes`);
      setWbsTree((wbsFull.data || []).map((n) => ({
        id: n.id, name: n.name,
        parent_id: n.parent_id || "",
        category: n.category,
      })));
      // Phases (Proje Yapısı) — flat with parent_id (blocks legacy hariç)
      const phaseFlat = [];
      for (const p of (phaseR.data || [])) {
        phaseFlat.push({
          id: p.id, name: p.name,
          parent_id: p.parent_id || "",
          category: p.category || "Etap",
        });
      }
      setPhases(phaseFlat);
      setTeams(teamR.data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  // Faz 30.5: Yaprak (leaf) tespiti — sadece en alt kırılım seçilebilir
  const wbsLeafIds = useMemo(() => {
    const hasChild = new Set();
    (wbsTree || []).forEach((n) => { if (n.parent_id) hasChild.add(n.parent_id); });
    return new Set((wbsTree || []).filter((n) => !hasChild.has(n.id)).map((n) => n.id));
  }, [wbsTree]);
  const phaseLeafIds = useMemo(() => {
    const hasChild = new Set();
    (phases || []).forEach((p) => { if (p.parent_id) hasChild.add(p.parent_id); });
    return new Set((phases || []).filter((p) => !hasChild.has(p.id)).map((p) => p.id));
  }, [phases]);

  // ---- Client filtering ----
  const filtered = useMemo(() => {
    let out = items;
    if (fWbs !== "all") out = out.filter((r) => r.wbs_node_id === fWbs);
    if (fLoc !== "all") out = out.filter((r) => (r.location_id || "") === (fLoc === "root" ? "" : fLoc));
    if (fTeam !== "all") out = out.filter((r) => r.team_id === (fTeam === "none" ? "" : fTeam));
    if (fStatus !== "all") out = out.filter((r) => r.status === fStatus);
    if (q.trim()) {
      const nq = q.toLowerCase();
      out = out.filter((r) =>
        (r.name || "").toLowerCase().includes(nq) ||
        (r.wbs_path || "").toLowerCase().includes(nq) ||
        (r.location_label || "").toLowerCase().includes(nq) ||
        (r.team_name || "").toLowerCase().includes(nq)
      );
    }
    return out;
  }, [items, q, fWbs, fLoc, fTeam, fStatus]);

  // ---- Sıralama ----
  const wiSort = useSortableRows(filtered, {
    defaultKey: "",
    getters: {
      name: (r) => r?.name,
      location: (r) => r?.location_name,
      quantity: (r) => Number(r?.quantity) || 0,
      unit_price: (r) => Number(r?.unit_price) || 0,
      progress: (r) => Number(r?.progress_percent) || 0,
      actual_qty: (r) => Number(r?.actual_quantity) || 0,
      earned: (r) => (Number(r?.quantity) || 0) * (Number(r?.unit_price) || 0) * ((Number(r?.progress_percent) || 0) / 100),
      team: (r) => r?.team_name,
      status: (r) => r?.status,
    },
  });

  // ---- Handlers ----
  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDlgOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      wbs_node_id: r.wbs_node_id, location_id: r.location_id || "",
      name: r.name, unit: r.unit, quantity: r.quantity,
      unit_price: r.unit_price,
      planned_start: r.planned_start || "", planned_finish: r.planned_finish || "",
      progress_percent: r.progress_percent || 0,
      team_id: r.team_id || "", team_id_manual: r.team_id_manual || false,
      status: r.status, description: r.description || "",
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.wbs_node_id) return toast.error("WBS pozu seçiniz");
    try {
      const payload = { ...form, site_id: siteId };
      payload.quantity = Number(payload.quantity) || 0;
      payload.unit_price = Number(payload.unit_price) || 0;
      payload.progress_percent = Number(payload.progress_percent) || 0;
      if (editing) {
        await axios.put(`${API}/work-items/${editing.id}`, payload);
        toast.success("Work Item güncellendi");
      } else {
        await axios.post(`${API}/work-items`, payload);
        toast.success("Work Item oluşturuldu");
      }
      setDlgOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "Work Item Sil",
      description: `"${r.name}" (${r.location_label}) silinsin mi?`,
      confirmText: "Sil", variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/work-items/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reassignTeams = async () => {
    try {
      const { data } = await axios.post(`${API}/work-items/reassign-teams/${siteId}`);
      toast.success(`Otomatik ekip ataması: ${data.updated} / ${data.total} güncellendi`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openBulk = () => {
    setBulkWbs([]); setBulkLoc([]);
    setBulkForm({
      default_quantity: 0, default_unit: "m2", default_unit_price: 0,
      default_planned_start: "", default_planned_finish: "",
    });
    setBulkOpen(true);
  };
  const runBulk = async () => {
    if (bulkWbs.length === 0) return toast.error("En az bir WBS düğümü seçiniz");
    try {
      const payload = {
        site_id: siteId,
        wbs_node_ids: bulkWbs,
        location_ids: bulkLoc,
        default_quantity: Number(bulkForm.default_quantity) || 0,
        default_unit: bulkForm.default_unit,
        default_unit_price: Number(bulkForm.default_unit_price) || 0,
        default_planned_start: bulkForm.default_planned_start,
        default_planned_finish: bulkForm.default_planned_finish,
        skip_existing: true,
      };
      const { data } = await axios.post(`${API}/work-items/bulk`, payload);
      toast.success(`${data.created} Work Item oluşturuldu (${data.skipped} atlandı)`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title={site ? `${site.name} — Work Item Yönetimi` : "Yükleniyor…"}
      subtitle="Proje Yönetimi › Work Item Yönetimi"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <Button variant="outline" onClick={reassignTeams}
                      data-testid="wi-reassign"
                      className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
                <ArrowsClockwise size={14} className="mr-1.5" /> Ekip Ata
              </Button>
              <Button variant="outline" onClick={openBulk}
                      data-testid="wi-bulk-btn"
                      className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
                <Stack size={14} className="mr-1.5" /> Toplu Oluştur
              </Button>
              <Button onClick={openNew} data-testid="wi-new"
                      className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea01a] text-white h-9 text-xs uppercase tracking-widest">
                <Plus size={14} className="mr-1.5" /> Yeni
              </Button>
            </>
          )}
          <Link to={`/proje/${siteId}/ilerleme`}>
            <Button variant="outline" data-testid="wi-go-progress"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ChartLineUp size={14} className="mr-1.5" /> İlerleme
            </Button>
          </Link>
          <Link to="/proje/work-items">
            <Button variant="outline" className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest"
                    data-testid="wi-back">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      }
    >
      {/* KPI */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wi-kpi">
        <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#1B4776]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Toplam Work Item</div>
          <div className="text-3xl font-bold text-[#1B4776] mt-1 font-heading">{items.length}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Ortalama İlerleme</div>
          <div className="text-3xl font-bold text-[#556B10] mt-1 font-heading">
            {fmt(totals.avg_progress, 1)}%
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Toplam Bedel</div>
          <div className="text-2xl font-bold text-[#1B4776] mt-1 font-heading">
            ₺{fmt(totals.total_amount)}
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Kazanılan (Earned)</div>
          <div className="text-2xl font-bold text-[#556B10] mt-1 font-heading">
            ₺{fmt(totals.earned_amount)}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3 rounded-none sm:rounded-sm mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} weight="bold"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="wi-search"
                   placeholder="Ara: iş adı, WBS yolu, lokasyon, ekip..."
                   className="pl-8 h-9 rounded-none sm:rounded-sm text-xs" />
          </div>
          <Select value={fWbs} onValueChange={setFWbs}>
            <SelectTrigger className="h-9 w-52 rounded-none sm:rounded-sm text-xs" data-testid="wi-filter-wbs">
              <SelectValue placeholder="WBS Filtresi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm WBS Pozları</SelectItem>
              {leaves.map((l) => (
                <SelectItem key={l.node_id} value={l.node_id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fLoc} onValueChange={setFLoc}>
            <SelectTrigger className="h-9 w-52 rounded-none sm:rounded-sm text-xs" data-testid="wi-filter-loc">
              <SelectValue placeholder="Lokasyon Filtresi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Lokasyonlar</SelectItem>
              <SelectItem value="root">— Proje Kökü —</SelectItem>
              {phases.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fTeam} onValueChange={setFTeam}>
            <SelectTrigger className="h-9 w-44 rounded-none sm:rounded-sm text-xs" data-testid="wi-filter-team">
              <SelectValue placeholder="Ekip" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Ekipler</SelectItem>
              <SelectItem value="none">— Ekipsiz —</SelectItem>
              {teams.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="h-9 w-40 rounded-none sm:rounded-sm text-xs" data-testid="wi-filter-status">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-none sm:rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="wi-table">
            <thead>
              <tr className="bg-[#1B4776] text-white text-[10px] uppercase tracking-widest">
                <SortableTh label="İş / WBS" k="name" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" className="text-left px-3 py-2 font-bold" />
                <SortableTh label="Lokasyon" k="location" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" className="text-left px-2 py-2 font-bold" />
                <SortableTh label="Metraj" k="quantity" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="right" className="text-right px-2 py-2 font-bold w-24" />
                <SortableTh label="Birim Fiy." k="unit_price" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="right" className="text-right px-2 py-2 font-bold w-24" />
                <SortableTh label="İlerleme" k="progress" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="right" className="text-right px-2 py-2 font-bold w-20" />
                <SortableTh label="Gerç. Miktar" k="actual_qty" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="right" className="text-right px-2 py-2 font-bold w-28" />
                <SortableTh label="Kazanılan ₺" k="earned" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="right" className="text-right px-2 py-2 font-bold w-28" />
                <SortableTh label="Ekip" k="team" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" className="text-left px-2 py-2 font-bold w-36" />
                <SortableTh label="Durum" k="status" sort={wiSort.sort} onSort={wiSort.toggleSort} tone="dark" align="center" className="text-center px-2 py-2 font-bold w-24" />
                <th className="text-center px-2 py-2 font-bold w-20">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={10} className="text-center py-12 text-slate-500">Yükleniyor…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-16 text-slate-500">
                  {items.length === 0
                    ? "Henüz Work Item yok. \"Toplu Oluştur\" ile başlayın."
                    : "Filtreye uygun kayıt bulunamadı."}
                </td></tr>
              ) : wiSort.sortedRows.map((r, i) => {
                const status = STATUS_META[r.status] || STATUS_META.tanimlandi;
                return (
                  <tr key={r.id} data-testid={`wi-row-${i}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900 truncate max-w-md">{r.name}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-md" title={r.wbs_path}>
                        <TreeStructure size={9} className="inline mr-0.5" />{r.wbs_path}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                        r.location_id ? "bg-blue-50 text-[#1B4776]" : "bg-slate-100 text-slate-500"
                      }`}>
                        <MapTrifold size={9} />{r.location_label}
                      </span>
                    </td>
                    <td className="text-right px-2 py-2 font-mono font-bold text-slate-800">
                      {fmt(r.quantity)} <span className="text-[10px] text-slate-500">{r.unit}</span>
                    </td>
                    <td className="text-right px-2 py-2 font-mono text-slate-700">
                      ₺{fmt(r.unit_price)}
                    </td>
                    <td className="text-right px-2 py-2 font-mono font-bold">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm ${
                        r.progress_percent >= 100 ? "bg-emerald-100 text-emerald-700"
                          : r.progress_percent > 0 ? "bg-[#F7FBE9] text-[#556B10]"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {fmt(r.progress_percent, 1)}%
                      </span>
                    </td>
                    <td className="text-right px-2 py-2 font-mono text-slate-700">
                      {fmt(r.actual_quantity)} <span className="text-[10px] text-slate-400">/ {fmt(r.quantity)}</span>
                    </td>
                    <td className="text-right px-2 py-2 font-mono font-bold text-[#556B10]">
                      ₺{fmt(r.earned_amount)}
                    </td>
                    <td className="px-2 py-2">
                      {r.team_name ? (
                        <span className="inline-flex items-center gap-1">
                          <UsersFour size={11} className="text-[#1B4776]" />
                          <span className="text-[11px] font-semibold text-slate-800 truncate">{r.team_name}</span>
                          {r.team_id_manual && (
                            <span className="text-[8px] uppercase tracking-widest font-bold text-orange-600 bg-orange-50 px-1 rounded-sm">
                              Manuel
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">Atanmadı</span>
                      )}
                    </td>
                    <td className="text-center px-2 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-widest font-bold ${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="text-center px-2 py-2">
                      <div className="inline-flex items-center gap-0.5">
                        {canManage && (
                          <>
                            <button onClick={() => openEdit(r)} data-testid={`wi-edit-${i}`}
                                    className="p-1 text-slate-500 hover:text-[#1B4776] hover:bg-slate-100 rounded-sm">
                              <PencilSimple size={12} />
                            </button>
                            <button onClick={() => remove(r)} data-testid={`wi-del-${i}`}
                                    className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-sm">
                              <TrashSimple size={12} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit / New Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-2xl rounded-none sm:rounded-sm" data-testid="wi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={16} className="text-[#556B10]" />
              {editing ? "Work Item Düzenle" : "Yeni Work Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">WBS Pozu *</Label>
              <Select value={form.wbs_node_id}
                      onValueChange={(v) => setForm((f) => ({ ...f, wbs_node_id: v, name: leaves.find(l=>l.node_id===v)?.name || f.name }))}
                      disabled={!!editing}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="wi-form-wbs">
                  <SelectValue placeholder="WBS yaprak seçin" />
                </SelectTrigger>
                <SelectContent>
                  {leaves.map((l) => (
                    <SelectItem key={l.node_id} value={l.node_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Lokasyon</Label>
              <Select value={form.location_id || "__root__"}
                      onValueChange={(v) => setForm((f) => ({ ...f, location_id: v === "__root__" ? "" : v }))}
                      disabled={!!editing}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1" data-testid="wi-form-loc">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__root__">— Proje Kökü (site-level)</SelectItem>
                  {phases.map((p) => {
                    const isLeaf = phaseLeafIds.has(p.id);
                    return (
                      <SelectItem key={p.id} value={p.id} disabled={!isLeaf}
                                  className={!isLeaf ? "opacity-50 italic" : ""}>
                        {p.name}
                        {!isLeaf && <span className="ml-2 text-[9px] uppercase tracking-widest text-slate-400">(alt kırılım — özet)</span>}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Birim</Label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9" placeholder="m2, m3, ad..." />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Metraj</Label>
              <Input type="number" step="0.01" value={form.quantity}
                     data-testid="wi-form-qty"
                     onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 font-mono text-right" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Birim Fiyat ₺</Label>
              <Input type="number" step="0.01" value={form.unit_price}
                     onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 font-mono text-right" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İlerleme %</Label>
              <Input type="number" step="0.1" min="0" max="100" value={form.progress_percent}
                     data-testid="wi-form-progress"
                     onChange={(e) => setForm({ ...form, progress_percent: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 font-mono text-right" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Planlanan Başlangıç</Label>
              <Input type="date" value={form.planned_start}
                     onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Planlanan Bitiş</Label>
              <Input type="date" value={form.planned_finish}
                     onChange={(e) => setForm({ ...form, planned_finish: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">
                Ekip (Manuel Override)
              </Label>
              <Select value={form.team_id || "__none__"}
                      onValueChange={(v) => setForm({ ...form, team_id: v === "__none__" ? "" : v, team_id_manual: true })}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Otomatik / Yok</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-none sm:rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Açıklama</Label>
              <Textarea value={form.description} rows={2}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="rounded-none sm:rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="wi-save"
                    className="bg-[#95C11F] hover:bg-[#7ea01a] text-white">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl rounded-none sm:rounded-sm" data-testid="wi-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stack size={16} className="text-[#95C11F]" />
              Toplu Work Item Oluştur
            </DialogTitle>
            <DialogDescription className="text-xs">
              Seçili WBS pozları × seçili lokasyonlar kartezyeni ile Work Item'lar oluşturulur.
              Mevcut kayıtlar atlanır.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {/* WBS Selector */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mb-1 flex items-center gap-1">
                <TreeStructure size={11} /> WBS Kapsamı ({bulkWbs.length} seçili)
              </div>
              <div className="border border-slate-200 rounded-none sm:rounded-sm max-h-72 overflow-y-auto p-1"
                   data-testid="wi-bulk-wbs-tree">
                <TreePicker nodes={wbsTree} selected={bulkWbs}
                            onToggle={(id) => setBulkWbs((v) =>
                              v.includes(id) ? v.filter((x) => x !== id) : [...v, id])}
                            testIdPrefix="wi-bulk-wbs"
                            leafIds={wbsLeafIds}
                            emptyLabel="WBS boş — önce WBS Kırınımları'nda düğüm ekleyin." />
              </div>
              <div className="text-[10px] text-slate-500 mt-1 italic">
                Bir üst düğüm seçilirse (turuncu <b>⇩ ALT KIRILIMLAR</b> etiketi) tüm alt yapraklar dahil edilir.
                Work Item'lar yalnızca en alt seviyeye (yaprak WBS'e) atanır.
              </div>
            </div>
            {/* Location Selector */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B4776] mb-1 flex items-center gap-1">
                <MapTrifold size={11} /> Lokasyon Kapsamı ({bulkLoc.length} seçili)
              </div>
              <div className="border border-slate-200 rounded-none sm:rounded-sm max-h-72 overflow-y-auto p-1"
                   data-testid="wi-bulk-loc-tree">
                <TreePicker nodes={phases} selected={bulkLoc}
                            onToggle={(id) => setBulkLoc((v) =>
                              v.includes(id) ? v.filter((x) => x !== id) : [...v, id])}
                            testIdPrefix="wi-bulk-loc"
                            leafIds={phaseLeafIds}
                            emptyLabel="Proje Yapısı boş — bir Etap/Blok tanımlayın." />
              </div>
              <div className="text-[10px] text-slate-500 mt-1 italic">
                Hiç lokasyon seçilmezse Proje Kökü (site-level) kullanılır.
                Üst kırılım seçilirse (turuncu <b>⇩ ALT KIRILIMLAR</b>) yaprak lokasyonlara otomatik dağıtılır.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-2 pt-3 border-t border-slate-200">
            <div>
              <Label className="text-[9px] uppercase tracking-widest font-bold text-slate-600">Ön Metraj</Label>
              <Input type="number" step="0.01" value={bulkForm.default_quantity}
                     onChange={(e) => setBulkForm({ ...bulkForm, default_quantity: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 font-mono text-right text-xs" />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest font-bold text-slate-600">Birim</Label>
              <Input value={bulkForm.default_unit}
                     onChange={(e) => setBulkForm({ ...bulkForm, default_unit: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 text-xs" placeholder="m2" />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest font-bold text-slate-600">Birim Fiy. ₺</Label>
              <Input type="number" step="0.01" value={bulkForm.default_unit_price}
                     onChange={(e) => setBulkForm({ ...bulkForm, default_unit_price: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 font-mono text-right text-xs" />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest font-bold text-slate-600">Başl.</Label>
              <Input type="date" value={bulkForm.default_planned_start}
                     onChange={(e) => setBulkForm({ ...bulkForm, default_planned_start: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 text-xs" />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
              <Input type="date" value={bulkForm.default_planned_finish}
                     onChange={(e) => setBulkForm({ ...bulkForm, default_planned_finish: e.target.value })}
                     className="rounded-none sm:rounded-sm mt-1 h-9 text-xs" />
            </div>
          </div>
          <div className="text-[11px] font-bold text-[#1B4776] p-2 bg-[#F7FBE9] border-l-4 border-[#95C11F]">
            Öngörülen: {bulkWbs.length} WBS × {Math.max(1, bulkLoc.length)} lokasyon = <b className="text-[#556B10]">
              {bulkWbs.length * Math.max(1, bulkLoc.length)} Work Item</b> (mevcutlar atlanır)
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>İptal</Button>
            <Button onClick={runBulk} data-testid="wi-bulk-run"
                    className="bg-[#95C11F] hover:bg-[#7ea01a] text-white">
              <Stack size={12} className="mr-1" /> Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
